import { useId, useState } from "react";

import { AlertCircle, Check, Copy, LoaderCircle } from "lucide-react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@perceptron/ui/components/tooltip";

import type { TimelinePlayerBridge } from "../../bridge";
import { useTrackingInspectorStore } from "./controller";
import { TRACKING_SOURCE_APPEARANCES } from "./sourceAppearance";
import type { TrackingInspectorRow, TrackingInspectorSnapshot } from "./status";
import { formatConfidence, serializeTrackingDiagnostics } from "./tools";
import { TrackFrameNavigation } from "./TrackFrameNavigation";
import { TrackDiagnostics } from "./TrackDiagnostics";

const copy = strings.trackingInspector;

export function TrackDetail({
  bridge,
  row,
  snapshot,
}: {
  bridge: TimelinePlayerBridge;
  row: TrackingInspectorRow;
  snapshot: TrackingInspectorSnapshot;
}) {
  const activeSettings = useTrackingInspectorStore((state) => state.activeSettings);
  const [feedback, setFeedback] = useState<{ trackId: string; error: boolean } | null>(null);
  const navigationHintId = useId();
  const navigationHint = !row.anchorTimes
    ? copy.navigationNoInterval
    : !row.frameTimes?.length
      ? copy.navigationNoFrames
      : copy.navigationHint;
  const hasChosenConfidence = row.method === "sparse" || row.method === "nano" || row.method === "hybrid";
  const chosenConfidence = hasChosenConfidence && row.frame ? row.frame.confidence : null;
  const appearance =
    row.method && Object.hasOwn(TRACKING_SOURCE_APPEARANCES, row.method)
      ? TRACKING_SOURCE_APPEARANCES[row.method as keyof typeof TRACKING_SOURCE_APPEARANCES]
      : undefined;
  const source =
    row.method && row.method in copy.sources
      ? copy.sources[row.method as keyof typeof copy.sources]
      : row.frame?.method === "unavailable"
        ? copy.sources.unavailable
        : copy.none;
  const isPreview = row.phase && row.phase !== "final" && row.method && row.method !== "anchor";
  const pending = row.status === "queued" || row.status === "processing";
  const showReason = !appearance || row.status === "error";
  const currentFeedback = feedback?.trackId === row.trackId ? feedback : null;
  const copyDiagnostics = async () => {
    setFeedback(null);
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(serializeTrackingDiagnostics(snapshot, row, activeSettings));
      setFeedback({ trackId: row.trackId, error: false });
    } catch {
      setFeedback({ trackId: row.trackId, error: true });
    }
  };

  return (
    <section aria-label={copy.selectedTrack} className="min-w-0 space-y-3" data-tracking-selected-track={row.trackId}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="break-words text-sm font-medium leading-5">{row.label}</h4>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              aria-label={copy.copyDiagnostics}
              className="size-8 shrink-0 text-foreground-secondary"
              size="icon"
              type="button"
              variant="ghost"
              onClick={() => void copyDiagnostics()}
            >
              {currentFeedback && !currentFeedback.error ? (
                <Check aria-hidden="true" className="size-4" />
              ) : (
                <Copy aria-hidden="true" className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} theme="popover">
            {currentFeedback && !currentFeedback.error ? copy.copied : copy.copyDiagnostics}
          </TooltipContent>
        </Tooltip>
      </div>
      {currentFeedback && (
        <p
          className={currentFeedback.error ? "break-words text-xs text-error" : "sr-only"}
          role={currentFeedback.error ? "alert" : "status"}
        >
          {currentFeedback.error ? copy.copyFailed : copy.copied}
        </p>
      )}

      <div className="space-y-2 rounded-lg border border-border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium">
            {appearance ? (
              <Check aria-hidden="true" className="size-3.5" />
            ) : pending ? (
              <LoaderCircle aria-hidden="true" className="size-3.5 animate-spin motion-reduce:animate-none" />
            ) : (
              <AlertCircle aria-hidden="true" className="size-3.5 text-foreground-secondary" />
            )}
            {appearance ? copy.resultAvailable : pending ? copy.resultPending : copy.resultUnavailable}
          </span>
          {chosenConfidence !== null && (
            <span
              aria-label={`${copy.confidence}: ${formatConfidence(chosenConfidence)}`}
              className="text-xs tabular-nums text-foreground-secondary"
            >
              {formatConfidence(chosenConfidence)}
            </span>
          )}
        </div>
        {appearance && (
          <div className="flex flex-wrap items-center gap-1.5">
            <dl>
              <dt className="sr-only">{copy.source}</dt>
              <dd className="inline-flex items-center gap-1.5 rounded-md bg-background-alt-2 px-2 py-1 text-xs">
                <span aria-hidden="true" className="size-2 rounded-sm" style={{ backgroundColor: appearance.color }} />
                {source}
              </dd>
            </dl>
            {isPreview && <span className="text-xs text-foreground-secondary">{copy.preview}</span>}
            {row.frame?.recovery && row.method !== "model-recovery" && (
              <span className="text-xs text-foreground-secondary">{copy.recovery.assisted}</span>
            )}
          </div>
        )}
        {showReason && (
          <p
            className="whitespace-pre-wrap break-words text-xs leading-relaxed text-foreground-secondary"
            role={row.status === "error" ? "alert" : undefined}
          >
            {row.detail}
          </p>
        )}
        {row.recovery?.status === "failed" && row.recovery.error && row.recovery.error !== row.detail && (
          <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-error" role="alert">
            {row.recovery.error}
          </p>
        )}
      </div>

      <TrackFrameNavigation
        bridge={bridge}
        currentTime={snapshot.currentTime}
        hint={{ id: navigationHintId, text: navigationHint }}
        row={row}
      />

      <TrackDiagnostics
        currentTime={snapshot.currentTime}
        hasChosenConfidence={hasChosenConfidence}
        hasSource={!!appearance}
        navigationHint={{ id: navigationHintId, text: navigationHint }}
        row={row}
        showReason={showReason}
        source={source}
      />
    </section>
  );
}
